/* ==================================================================
   The Golden Quest · quick survey helpers
   The survey is a short branching questionnaire from the campaign API
   (`goldenQuest.survey`): one opening question whose answer picks a
   "door" — the set of follow-up questions — and a Golden Chest for
   finishing. Answers are kept by question id: the option index for a
   single-choice question, the list of option indexes for a multi-choice
   one. These helpers are shared by the hub card, the rewards page, the
   survey screen and the API layer.
   ================================================================== */

/* "1 question" / "3 questions" */
export const qWord = (n) => `${n} question${n === 1 ? '' : 's'}`;

/* The backend's resume copy carries a count placeholder ("You're 0 questions
   away from your Golden Chest"); swap the real remaining count in. */
export const fillCount = (text, n) => (text || '').replace(/\d+\s+questions?/, qWord(n));

/* has this question been answered? */
export const isAnswered = (a) => (Array.isArray(a) ? a.length > 0 : a !== null && a !== undefined);

/* the door the opening answer picks: the doors are keyed "1", "2", "3" in
   the order of the opening question's options */
export function doorFor(survey, answers) {
  const v = answers && answers.q1;
  if (!Number.isInteger(v)) return null;
  const byOrder = String(v + 1);
  if (survey.doors[byOrder]) return byOrder;
  return Object.keys(survey.doors)[v] || null;
}

/* the questions in order: the opening one, then the chosen door's */
export function surveyPath(survey, door) {
  return [survey.q1].concat(door && survey.doors[door] ? survey.doors[door] : []);
}

/* the longest path — what the progress bars show until the door is known */
export function surveyMaxLength(survey) {
  const doors = Object.values(survey.doors || {});
  return 1 + doors.reduce((m, d) => Math.max(m, d.length), 0);
}

/* the shortest path — for "4–5 questions" */
export function surveyMinLength(survey) {
  const doors = Object.values(survey.doors || {});
  return doors.length ? 1 + doors.reduce((m, d) => Math.min(m, d.length), Infinity) : 1;
}

/* The window has closed (`survey.endsAt`) and the user never started:
   the response left `status` out, or it is still "new". A survey that is
   in progress or done keeps that standing past the close, the way Quote &
   Claim keeps a post that is in review or approved. */
export function isSurveyEnded(survey, state, now = Date.now()) {
  if (!state) return false;
  if (state.status === 'ended') return true;
  if (state.status !== 'new' || !survey || !survey.endsAt) return false;
  const ends = survey.endsAt.getTime();
  return Number.isFinite(ends) && now >= ends;
}

/* { done, total, rem }: answered questions in a row from the start, out of
   the path (the longest path until the door is picked) */
export function surveyProgress(survey, { answers = {}, door = null } = {}) {
  const path = surveyPath(survey, door);
  const total = door ? path.length : surveyMaxLength(survey);
  let done = 0;
  for (let i = 0; i < path.length; i += 1) {
    if (isAnswered(answers[path[i].id])) done += 1;
    else break;
  }
  return { done, total, rem: Math.max(0, total - done) };
}
